import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsConsent } from '../../state/analyticsConsentStorage';
import useAnalytics from '../../hooks/useAnalytics';
import { useLoginState } from 'state/hooks/login.state.hook';
import '../cookie-consent/style.scss';

interface CookieSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (enableAnalytics: boolean) => void;
  variant?: 'modal' | 'inline';
}

const CookieSettingsDialog: React.FC<CookieSettingsDialogProps> = ({
  open,
  onClose,
  onSave,
  variant = 'inline',
}) => {
  const [enableAnalytics, setEnableAnalytics] = useState(true);
  const { t } = useTranslation();
  const { optOut, optIn } = useAnalytics();
  const { setShowCookieSettingsDialog } = useLoginState();

  useEffect(() => {
    if (open) {
      const currentAnalytics = getAnalyticsConsent();
      if (currentAnalytics !== null) {
        setEnableAnalytics(currentAnalytics);
      }
      if (variant === 'inline') {
        setShowCookieSettingsDialog(true);
      }
    }
  }, [open, variant]);

  const handleSaveSettings = useCallback(() => {
    if (enableAnalytics) {
      optIn();
    } else {
      optOut();
    }
    
    if (onSave) {
      onSave(enableAnalytics);
    }
    onClose();
  }, [enableAnalytics, onSave, onClose, optIn, optOut]);

  const handleToggleAnalytics = () => {
    setEnableAnalytics((prev) => !prev);
  };

  const handleOverlayClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleDialogClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const DialogContent = (
    <>
      <h3 style={{ color: 'black' }}>{t('cookieConsent.cookiesSettings')}</h3>
      <div>
        <div className="header">
          <h4 style={{ margin: 0, fontWeight: 500, color: 'black' }}>
            {t('cookieConsent.strictlyNecessaryCookies.title')}
          </h4>
          <label className="switch disabled">
            <input type="checkbox" checked />
            <span className="slider round"></span>
          </label>
        </div>
        <p style={{ fontSize: '14px', color: 'black', lineHeight: '1.5', fontWeight: 300 }}>
          {t('cookieConsent.strictlyNecessaryCookies.description')}
        </p>
      </div>
      <div>
        <div className="header">
          <h4 style={{ margin: 0, fontWeight: 500, color: 'black' }}>
            {t('cookieConsent.analyticsCookies.title')}
          </h4>
          <label className="switch">
            <input type="checkbox" checked={enableAnalytics} onChange={handleToggleAnalytics} />
            <span className="slider round"></span>
          </label>
        </div>
        <p style={{ fontSize: '14px', color: 'black', lineHeight: '1.5', fontWeight: 300 }}>
          {t('cookieConsent.analyticsCookies.description')}
        </p>
      </div>
      <button className="save-settings" onClick={handleSaveSettings}>
        {t('cookieConsent.saveSettings')}
      </button>
    </>
  );

  if (!open) return null;

  if (variant === 'modal') {
    return (
      <div id="cookieConsent" onClick={handleOverlayClick}>
        <div className="dialog" onClick={handleDialogClick}>
          {DialogContent}
        </div>
      </div>
    );
  }

  return (
    <div className="dialog" onClick={handleDialogClick}>
      {DialogContent}
    </div>
  );
};

export default CookieSettingsDialog;
